import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["theme", "children", "variant"],
    _excluded2 = ["variant", "children", "theme"],
    _excluded3 = ["children", "theme", "variant", "liProps"],
    _excluded4 = ["variant", "theme", "liProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import Box from '../Box';
import Link from '../Link';
import { BaseText } from '../Text/Text';
import { useThemeDefaults } from '../utils';
export var SIDE = 'side';
export var POPOVER = 'popover';
export var MOBILE = 'mobile';
var MenuVariantContext = /*#__PURE__*/React.createContext(SIDE);

var isSideVariant = function isSideVariant(variant) {
  return variant === SIDE;
};

var isMobileVariant = function isMobileVariant(variant) {
  return variant === MOBILE;
};

export var withVariant = function withVariant(Component) {
  return /*#__PURE__*/React.forwardRef(function (props, ref) {
    return /*#__PURE__*/React.createElement(MenuVariantContext.Consumer, null, function (variant) {
      return /*#__PURE__*/React.createElement(Component, _extends({
        variant: variant
      }, props, {
        ref: ref
      }));
    });
  });
};
var NavMenu = withTheme(function (_ref) {
  var theme = _ref.theme,
      children = _ref.children,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? SIDE : _ref$variant,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(Box, _extends({
    role: "navigation",
    pt: theme.navmenu.paddingTop,
    width: "100%"
  }, props), /*#__PURE__*/React.createElement(MenuVariantContext.Provider, {
    value: variant
  }, children));
});
NavMenu.propTypes = _objectSpread({
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE]).isRequired
}, Box.propTypes);
NavMenu.Title = withVariant(useThemeDefaults(function (_ref2) {
  var variant = _ref2.variant,
      theme = _ref2.theme;
  return {
    role: 'heading',
    color: 'title',
    textStyle: isMobileVariant(variant) ? theme.navmenu.titleTextStyleMobile : theme.navmenu.titleTextStyle,
    lineHeight: isSideVariant(variant) ? "".concat(theme.navmenu.itemHeightSide, "px") : isMobileVariant(variant) ? theme.navmenu.itemHeightMobile : theme.navmenu.itemHeightPopover,
    ml: isSideVariant(variant) ? theme.navmenu.leftOffsetSide : theme.navmenu.leftOffset,
    borderBottom: isSideVariant(variant) ? 'none' : theme.navmenu.keylineBorder,
    borderColor: theme.navmenu.keylineColor
  };
}, BaseText));
NavMenu.Title.propTypes = _objectSpread(_objectSpread({}, BaseText.propTypes), {}, {
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE])
});
NavMenu.Links = withTheme(withVariant(function (_ref3) {
  var variant = _ref3.variant,
      children = _ref3.children,
      theme = _ref3.theme,
      props = _objectWithoutProperties(_ref3, _excluded2);

  return /*#__PURE__*/React.createElement(Box, _extends({
    as: "ul",
    mb: theme.navmenu.linksMarginBottom
  }, props), isSideVariant(variant) ? children : React.Children.map(children, function (child, i) {
    return i !== 0 ? /*#__PURE__*/React.cloneElement(child, {
      hasKeyline: true
    }) : child;
  }));
}));
NavMenu.Links.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), {}, {
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE])
});

var mediumWeightHover = function mediumWeightHover(_ref4) {
  var theme = _ref4.theme,
      disabled = _ref4.disabled,
      variant = _ref4.variant;
  return !disabled && !isMobileVariant(variant) && !isSideVariant(variant) ? {
    ':hover, :focus': {
      fontWeight: theme.fontWeights[theme.navmenu.hoverTextWeightNonMobile]
    }
  } : {};
};

var keylineStyles = function keylineStyles(_ref5) {
  var hasKeyline = _ref5.hasKeyline,
      active = _ref5.active,
      variant = _ref5.variant,
      theme = _ref5.theme;
  return !isSideVariant(variant) && hasKeyline && !active ? {
    ':hover': {
      '&::after': {
        visibility: 'hidden'
      }
    },
    '::after': {
      content: '""',
      position: 'absolute',
      height: theme.navmenu.keylineSize,
      top: '0px',
      right: '0px',
      left: isMobileVariant(variant) ? theme.navmenu.leftOffset : theme.navmenu.leftOffsetPopover,
      backgroundColor: theme.colors[theme.navmenu.keylineColor]
    }
  } : {};
};

var InnerLink = NavMenu.InnerLink = withVariant(useThemeDefaults(function (_ref6) {
  var active = _ref6.active,
      disabled = _ref6.disabled,
      variant = _ref6.variant,
      theme = _ref6.theme;
  return {
    as: !disabled ? 'a' : 'span',
    position: 'relative',
    display: 'block',
    hoverTextDecoration: 'none',
    hoverBg: disabled ? null : isSideVariant(variant) ? active ? theme.navmenu.linkActiveBgSide : 'transparent' : theme.navmenu.linkActiveBg,
    hoverColor: disabled ? null : isSideVariant(variant) ? theme.navmenu.hoverColorSide : theme.navmenu.hoverColor,
    bg: active ? isSideVariant(variant) ? theme.navmenu.linkActiveBgSide : theme.navmenu.linkActiveBg : null,
    color: disabled ? theme.navmenu.disabledColor : active && !isSideVariant(variant) ? theme.navmenu.hoverColor : theme.navmenu.color,
    borderRadius: isSideVariant(variant) ? theme.radius : '0px',
    lineHeight: isSideVariant(variant) ? "".concat(theme.navmenu.itemHeightSide - theme.navmenu.itemSidePaddingTop - theme.navmenu.itemSidePaddingBottom, "px") : isMobileVariant(variant) ? theme.navmenu.itemHeightMobile : theme.navmenu.itemHeightPopover,
    pl: isSideVariant(variant) ? theme.navmenu.leftOffsetSide : isMobileVariant(variant) ? theme.navmenu.leftOffset : theme.navmenu.leftOffsetPopover,
    textStyle: isMobileVariant(variant) ? theme.navmenu.itemTextStyleMobile : active ? theme.navmenu.itemTextStyleActive : theme.navmenu.itemTextStyle
  };
}, styled(Link).withConfig({
  displayName: "NavMenu__InnerLink",
  componentId: "sc-1vpbqzd-0"
})(mediumWeightHover, keylineStyles)));
InnerLink.propTypes = _objectSpread(_objectSpread({}, Link.propTypes), {}, {
  hasKeyline: PropTypes.bool,
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE]),
  active: PropTypes.bool,
  disabled: PropTypes.bool
});
NavMenu.Link = withVariant(withTheme( /*#__PURE__*/React.forwardRef(function (_ref7, ref) {
  var children = _ref7.children,
      theme = _ref7.theme,
      variant = _ref7.variant,
      liProps = _ref7.liProps,
      props = _objectWithoutProperties(_ref7, _excluded3);

  return /*#__PURE__*/React.createElement(Box, _extends({
    as: "li",
    paddingTop: isSideVariant(variant) ? "".concat(theme.navmenu.itemSidePaddingTop, "px") : '0px',
    paddingBottom: isSideVariant(variant) ? "".concat(theme.navmenuitemSidePaddingBottom, "px") : '0px'
  }, liProps), /*#__PURE__*/React.createElement(InnerLink, _extends({
    ref: ref
  }, props), children));
})));
NavMenu.Link.propTypes = _objectSpread(_objectSpread({}, InnerLink.propTypes), {}, {
  liProps: PropTypes.object,
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE])
});
NavMenu.CTALink = withVariant(withTheme(function (_ref8) {
  var variant = _ref8.variant,
      theme = _ref8.theme,
      liProps = _ref8.liProps,
      props = _objectWithoutProperties(_ref8, _excluded4);

  return /*#__PURE__*/React.createElement(Box, _extends({
    as: "li",
    textStyle: isMobileVariant(variant) ? theme.navmenu.itemTextStyleMobile : theme.navmenu.itemTextStyle,
    pl: isSideVariant(variant) ? theme.navmenu.leftOffsetSide : isMobileVariant(variant) ? theme.navmenu.leftOffset : theme.navmenu.leftOffsetPopover
  }, liProps), /*#__PURE__*/React.createElement(Link, props));
}));
NavMenu.CTALink.propTypes = _objectSpread(_objectSpread({}, Link.propTypes), {}, {
  liProps: PropTypes.object,
  variant: PropTypes.oneOf([SIDE, POPOVER, MOBILE])
});
export default NavMenu;